import { useState } from "react";
import { Button, Dialog, DialogContent } from "@mui/material";
import { icons } from "constants/icons";
import { PaywallModal } from "components/PaywallModal";
import { ManagePlanModal } from "components/ManagePlanModal/ManagePlanModal";
import { useAppSelector } from "hooks/reduxHooks";
import { selectCurrentClinician } from "store/ClinicianStore/selectors";
import { createStripeCheckoutSession } from "repositories/coachApi";
import { USER_GROUPS } from "constants/roles";

type Props = {
  analysesBlocked?: number;
  onUpgrade?: () => void;
};

const upgradeBannerStyles = {
  gradientBanner: {
    background: "linear-gradient(87.29deg, #7068AF -42.06%, #EB2A33 120.03%)",
    borderRadius: "24px",
    padding: "16px 20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "24px",
    marginLeft: "16px",
    marginRight: "16px",
  },
  bannerTextContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  alertIcon: {
    width: "24px",
    height: "24px",
  },
  bannerText: {
    color: "#FFFFFF",
    fontSize: "16px",
    fontWeight: "600",
    fontFamily: "SofiaPro",
    margin: 0,
  },
  upgradeButton: {
    backgroundColor: "transparent",
    color: "#FFFFFF",
    padding: "8px 20px",
    borderRadius: "6px",
    fontSize: "18px",
    fontWeight: "700",
    fontFamily: "SofiaPro",
    textTransform: "none" as const,
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.15)",
      transform: "translateY(-1px)",
    },
  },
};

const creditsModalStyles = {
  container: {
    padding: 24,
  },
  title: {
    marginTop: 8,
    marginBottom: 16,
    fontSize: 30,
    fontWeight: 700,
    textAlign: "center" as const,
  },
  subtitle: {
    marginTop: 8,
    marginBottom: 16,
    color: "#6B7280",
    fontSize: 16,
    lineHeight: 1.5,
    textAlign: "left" as const,
  },
  bulletList: {
    marginTop: 8,
    marginBottom: 16,
    paddingLeft: 18,
    gap: 8,
    color: "#111827",
  },
  footerNote: {
    marginTop: 8,
    marginBottom: 20,
    color: "#6B7280",
    fontSize: 14,
  },
  actions: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 12,
  },
  buyMoreButton: {
    background: "linear-gradient(87.29deg, #7068AF -42.06%, #EB2A33 120.03%)",
    color: "#FFFFFF",
    borderRadius: 12,
    padding: "14px 20px",
    fontSize: 18,
    fontWeight: 700,
    textTransform: "none" as const,
  },
  upgradeOutlineButton: {
    borderRadius: 12,
    padding: "14px 20px",
    fontSize: 18,
    fontWeight: 700,
    textTransform: "none" as const,
  },
  closeRow: {
    display: "flex",
    justifyContent: "flex-end",
  },
  closeButton: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
  },
};

export const UpgradeBanner = ({ analysesBlocked = 7, onUpgrade }: Props) => {
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [showCreditsRanOutModal, setShowCreditsRanOutModal] = useState(true);
  const currentClinician = useAppSelector(selectCurrentClinician);
  const [location] = useState(window.location.pathname);

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      setShowManageModal(true);
    }
  };

  // Hide upgrade banner for super admins
  if (
    !currentClinician?.role?.includes(USER_GROUPS.COACH) ||
    currentClinician?.hasLifetimeAccess
  ) {
    return null;
  }

  if (
    (currentClinician?.subscription?.subscribed &&
      !currentClinician?.subscription?.creditsRanOut) ||
    location.includes("/subscription/success")
  ) {
    return null;
  }

  if (
    currentClinician?.subscription?.subscribed &&
    currentClinician?.subscription?.creditsRanOut
  ) {
    const handleBuyMore = async () => {
      setShowCreditsRanOutModal(false);
      const response = await createStripeCheckoutSession("3");
      if (response.data.url) {
        const url = response.data.url;
        window.location.href = url;
      }
    };

    return (
      <>
        <Dialog
          open={showCreditsRanOutModal}
          onClose={() => setShowCreditsRanOutModal(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: { borderRadius: "12px", maxWidth: "420px", margin: "auto" },
          }}
        >
          <DialogContent sx={{ padding: 0 }}>
            <div style={creditsModalStyles.container}>
              <div style={creditsModalStyles.closeRow}>
                <button
                  aria-label="Close"
                  style={creditsModalStyles.closeButton}
                  onClick={() => setShowCreditsRanOutModal(false)}
                >
                  ✕
                </button>
              </div>
              <h2 style={creditsModalStyles.title}>Unlock more LLM analyses</h2>
              <p style={creditsModalStyles.subtitle}>
                You’ve used all 50 AI analyses in your plan. Choose how you’d
                like to continue:
              </p>
              <ul style={creditsModalStyles.bulletList}>
                <li>
                  <strong>- Buy More</strong> – Add another 50 analyses for
                  $100.
                </li>
                <li>
                  <strong>- Upgrade</strong> – Unlock higher limits, lower cost
                  per analysis, and team features.
                </li>
              </ul>
              <p style={creditsModalStyles.footerNote}>
                Your analysis limit will reset with your next billing cycle.
                Upgrade now to keep analyzing without interruptions.
              </p>
              <div style={creditsModalStyles.actions}>
                <Button
                  onClick={handleBuyMore}
                  style={creditsModalStyles.buyMoreButton}
                >
                  Buy More
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setShowCreditsRanOutModal(false);
                    setShowPaywallModal(true);
                  }}
                  style={creditsModalStyles.upgradeOutlineButton}
                >
                  Upgrade
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Manage Plan Modal */}
        <ManagePlanModal
          open={showManageModal}
          onClose={() => setShowManageModal(false)}
          onUpgrade={() => {
            setShowManageModal(false);
            setShowPaywallModal(true);
          }}
        />

        {/* Paywall Modal */}
        <PaywallModal
          open={showPaywallModal}
          onDowngrade={() => {
            setShowPaywallModal(false);
            setShowManageModal(true);
          }}
          onClose={() => setShowPaywallModal(false)}
          athleteAttempts={
            currentClinician?.athletesBlocked as unknown as Array<{
              athleteId: string;
              athleteName: string;
              avatar?: string;
              attempts: number;
            }>
          }
          totalAthletes={
            (currentClinician?.athletesBlockedAmount ||
              analysesBlocked) as number
          }
        />
      </>
    );
  }

  return (
    <>
      <div style={upgradeBannerStyles.gradientBanner}>
        <div style={upgradeBannerStyles.bannerTextContainer}>
          <img
            src={icons.errorWhite}
            alt="alert"
            style={upgradeBannerStyles.alertIcon}
          />
          <p style={upgradeBannerStyles.bannerText}>
            {currentClinician?.subscription?.billingCycleCreditsUsed || 0}/5
            free monthly AI analyses used. Upgrade now to unlock more for your
            athletes.
          </p>
        </div>
        <Button
          style={upgradeBannerStyles.upgradeButton}
          onClick={handleUpgrade}
        >
          Upgrade
        </Button>
      </div>

      {/* Manage Plan Modal */}
      <ManagePlanModal
        open={showManageModal}
        onClose={() => setShowManageModal(false)}
        onUpgrade={() => {
          setShowManageModal(false);
          setShowPaywallModal(true);
        }}
      />

      {/* Paywall Modal */}
      <PaywallModal
        open={showPaywallModal}
        onDowngrade={() => {
          setShowPaywallModal(false);
          setShowManageModal(true);
        }}
        onClose={() => setShowPaywallModal(false)}
        athleteAttempts={
          currentClinician?.athletesBlocked as unknown as Array<{
            athleteId: string;
            athleteName: string;
            avatar?: string;
            attempts: number;
          }>
        }
        totalAthletes={analysesBlocked}
      />
    </>
  );
};
